import { getCleanUsername, getUserIconElement, getDomainTypeFromUsername } from "@/lib/username";
import { cn, generateTextShadow } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useNavigate } from "@tanstack/react-router";
import type { DomainType } from "@/lib/types/api";

interface UserNameProps {
  username?: string | null;
  fallback?: string;
  className?: string;
  iconSize?: string;
  showIcon?: boolean;
  textShadow?: boolean;
  textShadowSize?: number;
  style?: React.CSSProperties;
  isVerified?: boolean;
  shouldWarnUnverified?: boolean;
  verifiedIconSize?: string;
  suffix?: React.ReactNode;
  domainType?: DomainType;
}

export function UserName({
  username,
  fallback = "Anon",
  className,
  iconSize = "w-4 h-4",
  showIcon = true,
  textShadow = false,
  textShadowSize = 1.25,
  style,
  isVerified = false,
  shouldWarnUnverified = false,
  verifiedIconSize = "size-4.5",
  suffix,
  domainType,
}: UserNameProps) {
  const displayName = username ? getCleanUsername(username) : fallback;
  const resolvedDomainType = domainType ?? getDomainTypeFromUsername(username);
  const icon = showIcon ? getUserIconElement(resolvedDomainType, iconSize) : null;

  return (
    <p
      className={cn("inline-flex items-center gap-1 min-w-0", className)}
      {...(style && { style })}
    >
      <span
        className="truncate"
        style={
          textShadow
            ? { textShadow: generateTextShadow(textShadowSize) }
            : undefined
        }
      >
        {displayName}
      </span>
      {showIcon && icon && <span className="shrink-0">{icon}</span>}
      {isVerified && (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="rgb(0, 149, 246)"
          className={cn(verifiedIconSize, "shrink-0")}
        >
          <path
            fillRule="evenodd"
            d="M16.403 12.652a3 3 0 0 0 0-5.304 3 3 0 0 0-3.75-3.751 3 3 0 0 0-5.305 0 3 3 0 0 0-3.751 3.75 3 3 0 0 0 0 5.305 3 3 0 0 0 3.75 3.751 3 3 0 0 0 5.305 0 3 3 0 0 0 3.751-3.75Zm-2.546-4.46a.75.75 0 0 0-1.214-.883l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z"
            clipRule="evenodd"
          />
        </svg>
      )}
      {shouldWarnUnverified && !isVerified && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center justify-center shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
              aria-label="Verification status"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="size-4.5 text-yellow-400"
              >
                <path
                  fill-rule="evenodd"
                  d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495ZM10 5a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 5Zm0 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"
                  clip-rule="evenodd"
                />
              </svg>
            </button>
          </PopoverTrigger>
          <PopoverContent className="max-w-xs text-sm text-left">
            <p>
              This user is not verified. Link Twitter/X and at least 1
              other social to get verified and receive weekly rewards.
            </p>
          </PopoverContent>
        </Popover>
      )}
      {suffix}
    </p>
  );
}
